import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import SVG from 'react-inlinesvg';
import micOffIcon from '@lifesize/ux-assets/vectors/svgIcons/mic-off.svg';
import { MUTE_AUDIO_BUTTON, MUTE_AUDIO } from 'constants/lsqaConstants';
import Tooltip from 'components/Tooltip/Tooltip';
import MuteTooltip from 'components/InCall/MuteTooltip/MuteTooltip';
import Button from 'components/Button/Button';
import footerClasses from 'components/InCall/CallFooter.module.scss';
import LevelledIcon from './LevelledIcon';

class AudioButton extends React.Component {
  webConferencingButton = ({ isMinimized, onSwitchToComputerAudio }) => (
    <div>
      {
        (!isMinimized) &&
        <Button
          className={footerClasses.switchLabel}
          kind="text"
          onClick={() => onSwitchToComputerAudio()}
          {...MUTE_AUDIO_BUTTON}
          dataAttrs={{ 'data-testid': 'switch-to-computer-audio-link' }}
        >
          <FormattedMessage
            id="callFooter.PSTN.mute.label"
            defaultMessage="Switch to computer audio"
          />
        </Button>
      }
      <button
        onClick={() => onSwitchToComputerAudio()}
        className={footerClasses.inCallButtonActive}
        {...MUTE_AUDIO}
        data-testid="switch-to-computer-audio-btn"
      >
        <SVG src={micOffIcon} data-testid="micOffIcon" />
        <Tooltip
          location="top"
          style={{ bottom: '100%', marginBottom: 15 }}
          label={(
            <FormattedMessage
              id="callFooter.PSTN.mute.label"
              defaultMessage="Switch to computer audio"
            />
          )}
        />
      </button>
    </div>
  );

  mutedByServerButton = ({ notification, isMinimized }) => (
    <button
      className={`${footerClasses.inCallButtonMutedByServer} disabled`}
      {...MUTE_AUDIO}
      data-testid="server-muted-audio-btn"
    >
      <SVG src={micOffIcon} data-testid="micOffIcon" />
      <MuteTooltip
        notification={notification}
        isMinimized={isMinimized}
      />
    </button>
  );

  noAudioDevicesButton = ({ onAudioMuteClick, isAudioMuted, isMutedByServer }) => (
    <button
      className={footerClasses.inCallButtonActive}
      onClick={() => onAudioMuteClick(!isAudioMuted)}
      disabled={isMutedByServer}
      {...MUTE_AUDIO}
      data-testid="no-audio-btn"
    >
      <SVG src={micOffIcon} data-testid="micOffIcon" />
      <Tooltip
        location="top"
        style={{ bottom: '100%', marginBottom: 15 }} label={(
          <FormattedMessage
            id="components.incall.controls.audioMute.tooltip.noMics"
            defaultMessage="No microphone detected"
          />
        )}
      />
    </button>
  );

  audioMutedButton = ({ onAudioMuteClick, isAudioMuted, isMutedByServer, isModerator }) => (
    <button
      className={footerClasses.inCallButtonActive}
      onClick={() => {
        if (!isModerator) {
          onAudioMuteClick(!isAudioMuted);
        } else {
          onAudioMuteClick(false, isMutedByServer);
        }
      }
      }
      disabled={isMutedByServer && !isModerator}
      {...MUTE_AUDIO}
      data-testid="unmute-audio-btn"
    >
      <SVG src={micOffIcon} data-testid="micOffIcon" />
      <Tooltip
        location="top"
        style={{ bottom: '100%', marginBottom: 15 }} label={(
          <FormattedMessage
            id="components.incall.controls.audioMute.tooltip.on"
            defaultMessage="Unmute microphone"
          />
        )}
      />
    </button>
  );

  defaultAudioButton = ({ onAudioMuteClick, isMinimized, microphoneDeviceId, isAudioMuted }) => (
    <button
      className={footerClasses.inCallButton}
      onClick={() => onAudioMuteClick(!isAudioMuted)}
      {...MUTE_AUDIO}
      data-testid="mute-audio-btn"
    >
      <LevelledIcon isMinimized={isMinimized} microphoneDeviceId={microphoneDeviceId} iconClass={this.props.iconClass} />
      <Tooltip
        location="top"
        style={{ bottom: '100%', marginBottom: 15 }} label={(
          <FormattedMessage
            id="components.incall.controls.audioMute.tooltip.off"
            defaultMessage="Mute microphone"
          />
        )}
      />
    </button>
  );

  render() {
    const {
      isConnected,
      isWebConferencing,
      isMutedByServer,
      isAudioMuted,
      hasAudioDevices,
      isModerator
    } = this.props;

    if (isConnected) {
      if (isWebConferencing) {
        return <this.webConferencingButton {...this.props} />;
      } else if (isMutedByServer && !isModerator) {
        return <this.mutedByServerButton {...this.props} />;
      } else if (!hasAudioDevices) {
        return <this.noAudioDevicesButton {...this.props} />;
      } else if (isAudioMuted || (isMutedByServer && isModerator)) {
        return <this.audioMutedButton {...this.props} />;
      }

      return <this.defaultAudioButton {...this.props} />;
    }

    return <div />;
  }
}

AudioButton.propTypes = {
  onSwitchToComputerAudio: PT.func,
  onAudioMuteClick: PT.func,
  iconClass: PT.string,
  isConnected: PT.bool,
  isWebConferencing: PT.bool,
  isMinimized: PT.bool,
  isMutedByServer: PT.bool,
  isAudioMuted: PT.bool,
  hasAudioDevices: PT.bool,
  notification: PT.string,
  microphoneDeviceId: PT.string,
  isModerator: PT.bool
};

export default AudioButton;
